import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Box, Image, useToast } from '@chakra-ui/react';
import { Alert, AlertDescription, AlertIcon } from '@/components/v4';
import { Button as BsButton } from 'react-bootstrap';
import { useNavigate } from 'react-router-dom';

import {
    getLanguagesList,
    getTemplatesList,
    getTimezonesList,
    postResourceAccount,
} from '@/API/ResourceAccount';
import { getAppAuthByCompanyId, getAppAuthById } from '@/API/AppAuth';
import PictureViewer from 'components/pictureViewer/PictureViewer';
import noImage from 'assets/images/noImage.png';

import walkthroughIds from '../../users/walkthroughIds';
import FormBase from '@/components/forms/new/FormBase';
import ResourceAccountNameField from './formFields/ResourceAccountNameField';
import ResourceDisplayNameField from './formFields/ResourceDisplayNameField';
import ResourceAccountTimezoneField from './formFields/ResourceAccountTimeZoneField';
import ResourceAccountLanguageField from './formFields/ResourceAccountLanguageField';
import ResourceAccountTemplateField from './formFields/ResourceAccountTemplateField';
import SkeletonBuilderPage from './SkeletonBuilderPage';
import { PlatformEnums } from 'utils/platforms';

const initialValues = {
    name: '',
    displayName: '',
    timezone: '',
    language: '',
    template: '',
};

function ResourceAccBuilder() {
    const navigate = useNavigate();
    const toast = useToast();
    const dispatch = useDispatch();
    const { currentCompany } = useSelector((state) => state.navigationLists);

    const [initialDomain, setInitialDomain] = useState('');
    const [imagePath, setImagePath] = useState(noImage);
    const [isAppAuthLoading, setIsAppAuthLoading] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const [timezones, setTimezones] = useState([]);
    const [languages, setLanguages] = useState([]);
    const [templates, setTemplates] = useState([]);
    const [formState, setFormState] = useState('idle');
    const [selectedTemplate, setSelectedTemplate] = useState('');
    const [zoomed, setZoomed] = useState(false);
    const { darkMode } = useSelector((state) => state.settings);
    const { autoAttendant } = walkthroughIds;
    useEffect(() => {
        setIsLoading(true);
        Promise.all([
            getTimezonesList().then((res) => setTimezones(res.data)),
            getLanguagesList().then((res) => setLanguages(res.data)),
            getTemplatesList().then((res) => {
                setTemplates(res.data);
                // Product requirement: default to Basic, which is always id 0
                // (the first item in the list).
                const basicTemplate =
                    res.data.find((tpl) => tpl.id === 0) || res.data[0];
                if (basicTemplate) {
                    setSelectedTemplate(basicTemplate.id);
                    setImagePath(
                        `${import.meta.env.VITE_APP_IMAGE_BASE}/images/${
                            basicTemplate.image
                        }`,
                    );
                }
            }),
            getAppAuthByCompanyId(currentCompany).then(async (res) => {
                const mAppAuth = await res.data.find(
                    (a) =>
                        a?.appAuthTypeID === 'Microsoft' &&
                        a?.platformID !== PlatformEnums.GCC,
                )?.id;
                setIsAppAuthLoading(true);
                if (mAppAuth) {
                    getAppAuthById(mAppAuth).then((resp) => {
                        setInitialDomain(resp.data.initialDomain);
                        setIsAppAuthLoading(false);
                    });
                } else {
                    setIsAppAuthLoading(false);
                }
            }),
        ]).finally(() => setIsLoading(false));
    }, [currentCompany]);

    const componentLoadingState = isLoading || isAppAuthLoading;
    // if (componentLoadingState) {
    //     return (
    //         <SkeletonBuilderPage darkMode={darkMode} handleNavigateButton={() => navigate('/users')} />
    //     );
    // }

    // FormBase submission handler
    const handleSubmit = async (values, actions) => {
        setFormState('submitting');
        console.log('Form values:', values);
        try {
            await postResourceAccount({
                formData: {
                    ...values,
                    companyId: currentCompany,
                },
            });
            toast({
                title: 'Success',
                description: 'Builder has been created successfully',
                status: 'success',
                duration: 3000,
            });
            setTimeout(() => {
                dispatch({
                    type: 'CHANGE_CURRENT_SCOPE',
                    payload: 20,
                });
                navigate('/jobs');
            }, 500);
        } catch (err) {
            toast({
                title: 'Error',
                description: `Cannot create new builder: ${
                    err?.response?.data?.message || 'Invalid Permission'
                }`,
                status: 'error',
                duration: 3000,
            });
        } finally {
            setFormState('idle');
            actions.setSubmitting(false);
        }
    };

    return (
        <>
            <Box className="flex flex-col">
                <Box className="mx-auto mt-4 w-full">
                    <FormBase
                        initialValues={{
                            ...initialValues,
                            template: selectedTemplate,
                        }}
                        enableReinitialize
                        finalFormSubmit={handleSubmit}
                        header="Resource Account Builder"
                        subTitleHeader={true}
                        subTitleHeaderComponent={
                            <Alert
                                status="info"
                                maxWidth="895px"
                                width="100%"
                                className="mb-4">
                                <AlertIcon />
                                <AlertDescription>
                                    Please note Resource Accounts must be
                                    licensed separately following the creation.
                                </AlertDescription>
                            </Alert>
                        }
                        backLink={['/users', { state: { index: 0 } }]}
                        breadCrumbTrail={[
                            { label: 'Users', path: '/users' },
                            { label: 'Builder', path: '' },
                        ]}
                        ufBackBtn="builderBackBtn"
                        ufSaveBtn="builderSaveBtn">
                        {({ errors, touched, setFieldValue }) => (
                            <>
                                <Box pt="16px">
                                    <ResourceAccountNameField
                                        errors={errors}
                                        touched={touched}
                                        darkMode={darkMode}
                                        initialDomain={initialDomain}
                                    />
                                </Box>
                                <ResourceDisplayNameField
                                    errors={errors}
                                    touched={touched}
                                    darkMode={darkMode}
                                />
                                <ResourceAccountTimezoneField
                                    errors={errors}
                                    touched={touched}
                                    timezones={timezones}
                                    darkMode={darkMode}
                                />
                                <ResourceAccountLanguageField
                                    errors={errors}
                                    touched={touched}
                                    languages={languages}
                                    darkMode={darkMode}
                                />
                                {!componentLoadingState && (
                                    <ResourceAccountTemplateField
                                        errors={errors}
                                        touched={touched}
                                        templates={templates}
                                        darkMode={darkMode}
                                        value={selectedTemplate}
                                        onImageChange={(path) => {
                                            setImagePath(path);
                                        }}
                                    />
                                )}
                                <>
                                    <Box
                                        as="button"
                                        border="none"
                                        bg="none"
                                        display="flex"
                                        alignItems="center"
                                        justifyContent="center"
                                        cursor="pointer"
                                        pt="8px"
                                        pb="8px"
                                        onClick={() => setZoomed(true)}
                                        _focus={{ outline: 'none' }}>
                                        <img
                                            src={imagePath}
                                            alt="Template Preview"
                                            width="500px"
                                            height="300px"
                                            style={{ aspectRatio: '500/300' }}
                                        />
                                    </Box>
                                    {zoomed && (
                                        <Box
                                            position="fixed"
                                            top={0}
                                            left={0}
                                            width="100vw"
                                            height="100vh"
                                            bg="rgba(0,0,0,0.8)"
                                            display="flex"
                                            alignItems="center"
                                            justifyContent="center"
                                            zIndex={9999}
                                            onClick={() => setZoomed(false)}
                                            cursor="zoom-out">
                                            <Image
                                                src={imagePath}
                                                maxWidth="90vw"
                                                maxHeight="90vh"
                                                boxShadow="2xl"
                                                borderRadius="md"
                                                transition="transform 0.3s"
                                                style={{
                                                    transform: 'scale(1.05)',
                                                }}
                                            />
                                        </Box>
                                    )}
                                </>
                            </>
                        )}
                    </FormBase>
                </Box>
            </Box>
        </>
    );
}

export default ResourceAccBuilder;
